import React, { forwardRef } from 'react';
import { useFocusRing, useButton } from 'react-aria';
import { AvatarProps } from './avatar.types';
import * as S from './avatar.styles';

export const Avatar = forwardRef<HTMLDivElement, AvatarProps>((props, ref) => {
  const { image, name, size = 40, onPress, className } = props;
  const { src, alt } = image || {};

  const isClickable = typeof onPress === 'function';
  const accountInitials = name?.trim().split(/\s+/).slice(0, 2).map(w => w.charAt(0).toUpperCase()).join('') || '';

  // Explicitly type the ref as mutable
  const buttonRef = React.useRef<HTMLDivElement | null>(null);
  const { buttonProps } = useButton(
    {
      onPress,
      elementType: 'div',
      'aria-label': isClickable ? `${name}'s avatar` : undefined,
    },
    buttonRef
  );

  const { isFocusVisible, focusProps } = useFocusRing();

  // Combine refs when the avatar is clickable
  const combinedRef = React.useMemo(() => {
    if (isClickable) {
      return (node: HTMLDivElement) => {
        buttonRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      };
    }
    return ref;
  }, [ref, isClickable]);

  return (
    <S.Avatar
      ref={combinedRef}
      size={size}
      isFocusVisible={isFocusVisible}
      isClickable={isClickable}
      className={className}
      {...(isClickable ? buttonProps : {})}
      {...focusProps}
      data-testid={props['data-testid'] || 'avatar'}
    >
      {src ? <S.AvatarImage src={src} alt={alt || `${name}'s avatar`} /> : accountInitials}
    </S.Avatar>
  );
});

Avatar.displayName = 'Avatar';
